import { List, ListItem, Typography } from "@mui/material";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { scrollToElement, useIntersectionObserver } from "./logics";
import { NavTableOfContents, classes } from "./styles";

export interface IAnchorElements {
  anchorId: string;
  title: string;
}

type NavTableOfContentsProps = Parameters<typeof NavTableOfContents>["0"];
interface ITableOfContents extends NavTableOfContentsProps {
  anchorElements: IAnchorElements[];
  parentId?: string;
}

export const TableOfContents = ({ anchorElements, parentId, ...props }: ITableOfContents) => {
  const [activeId, setActiveId] = useState<string>();
  const [isBottom, setIsBottom] = useState(false);
  const [parentElement, setParentElement] = useState<HTMLElement | null>(null);

  useEffect(() => setParentElement(document.getElementById(parentId ?? "root")), []);

  /**
   * @Observer
   * We observe given Element to determine wich element is active in the displayed list.
   * An active element mean that the element matching the active flag
   * is the nearest element that we can see on the top of the screen.
   */
  useIntersectionObserver(setActiveId, isBottom, anchorElements, parentId);

  useEffect(() => {
    if (parentElement) {
      const callback = (event: Event) => {
        if (event.target instanceof HTMLElement) {
          const { offsetHeight, scrollTop, scrollHeight } = event.target;
          if (Math.round(offsetHeight + scrollTop) + 50 >= Math.round(scrollHeight) && !isBottom) {
            setIsBottom(true);
          } else if (isBottom) {
            setIsBottom(false);
          }
        }
      };
      parentElement.addEventListener("scroll", callback);
      return () => parentElement.removeEventListener("scroll", callback);
    }
  }, [isBottom, parentElement]);

  return (
    <NavTableOfContents aria-label="table-of-contents" className={classes.root} {...props}>
      <List>
        {anchorElements.map(({ anchorId, title }) => (
          <ListItem key={anchorId} className={anchorId === activeId ? classes.itemSelected : classes.item}>
            <Typography
              onClick={() => scrollToElement(anchorId)}
              variant={anchorId === activeId ? "subtitle2SemiBold" : "body1"}
              className={clsx({
                [classes.link]: true,
                [classes.textSelected]: anchorId === activeId,
              })}
            >
              {title}
            </Typography>
          </ListItem>
        ))}
      </List>
    </NavTableOfContents>
  );
};
